import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

// stuff for dialog - move if separating
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import Switch from '@material-ui/core/Switch';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';

import UserImportDialog from 'app/components/user/UserImportDialog';
import NewUserDialog from 'app/components/user/NewUserDialog';
import PaginatedTable from 'app/components/tables/PaginatedTable';

import { containerFull } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import tableFieldTypes from 'app/utils/TableFieldTypes';

const useListItemStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: theme.spacing((props.depth * 2)),
    }),
    listItemIcon: {
        minWidth: 0
    }
}));

const findChildIds = (children) => {

    let ids = [];

    children.forEach(c => {

        ids.push(c.id);

        if (c.children_with_count.length > 0) {
            ids = ids.concat(findChildIds(c.children_with_count));
        }
    })

    return ids;
}

const NestedLevelItem = (props) => {

    const { level, depth, levelsChecked, onCheck, onCheckChildren } = props;

    const classes = useListItemStyles({ depth: depth});

    const [ includeChildren, setIncludeChildren ] = useState(false);

    const isSelected = levelsChecked.indexOf(level.id) > -1 ;

    const childIds = (level.children_with_count.length === 0) ? 0 : useMemo(() => findChildIds(level.children_with_count), []);

    const handleIncludeChildren = () => {
        setIncludeChildren(! includeChildren);
        const ids = [...childIds];
        if (! includeChildren) ids.push(level.id);
        onCheckChildren(! includeChildren, ids);
    }

    return (
        <>
            <ListItem
                classes={{ root: classes.nested }}
                button
                dense
                onClick={() => onCheck(level.id)}
                >
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                    />
                </ListItemIcon>
                <ListItemText
                    primary={ level.title }
                    className={ classes.listItemText }
                />
                <ListItemSecondaryAction>
                    <Typography
                        display="inline"
                        color="textSecondary"
                        variant="caption"
                        >
                        Incl. Sub
                    </Typography>
                    <Switch
                        edge="end"
                        onChange={handleIncludeChildren}
                        checked={includeChildren}
                        disabled={level.children_with_count.length === 0}
                        color="primary"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            {level.children_with_count.map((child) => (
                <NestedLevelItem
                    key={child.id}
                    level={child}
                    depth={(depth + 1)}
                    levelsChecked={levelsChecked}
                    onCheck={onCheck}
                    onCheckChildren={onCheckChildren}
                />
            ))}
        </>
    )
}

const useFilterStyles = makeStyles(theme => ({
    subSection: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[200]
    },
    subSectionList: {
        backgroundColor: theme.palette.grey[200],
        padding: 0
    },
    subSectionLoading: {
        textAlign: 'center',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.grey[200]
    },
    dialogSpacing: {
        padding: theme.spacing(1)
    },
    noSpacing: {
        padding: 0
    },
    noMarginLeft: {
        marginLeft: 0
    },
    empty: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    noContentPadding: {
        padding: 0
    },
    entered: {
        overflowY: 'scroll'
    }
}));

const UserFilterDialog = (props) => {

    const classes = useFilterStyles()

    const { open, onClose, onFilter } = props;

    const [ filters, setFilters ] = useState([]);
    const [ levelsChecked, setLevelsChecked ] = useState([]);
    const [ groupsChecked, setGroupsChecked ] = useState([]);

    const [ groups, setGroups ] = useState([]);
    const [ levels, setLevels ] = useState([]);

    const [ groupsLoading, setGroupsLoading ] = useState(false);
    const [ levelsLoading, setLevelsLoading ] = useState(false);

    const [ toggleFilter, setToggleFilter ] = useState(false);

    const api = useApi()
    const { app } = useApp();

    // Get Functions ------------------------------------------

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/admin/group/group')
            .then(res => setGroups(res.groups))
            .finally(() => setGroupsLoading(false));
    }

    const getLevels = () => {
        setLevelsLoading(true);

        api.get('/admin/group/level/recursive/0')
            .then(res => setLevels(res.groups))
            .finally(() => setLevelsLoading(false));
    }

    const getFilters = () => {
        const newFilters = app.user.application_state.users.filters;
        const activeFilters = [];

        if (newFilters.groups && newFilters.groups instanceof Array && newFilters.groups.length > 0) {
            setGroupsChecked(newFilters.groups);
            activeFilters.push('groups')
        }

        if (newFilters.levels && newFilters.levels instanceof Array &&
            newFilters.levels.length > 0) {
            setLevelsChecked(newFilters.levels)
            activeFilters.push('membership_levels')
        } else {
            if (levels.length > 0) {

                const member_level_ids = findChildIds(levels.filter(l => l.members_level === true));
    
                if (member_level_ids.length > 0) {
           
                    setLevelsChecked(member_level_ids)
                    activeFilters.push('membership_levels')
                    
                }
            }
        }

        if (newFilters.deleted) activeFilters.push('deleted')

        setFilters(activeFilters);
        setToggleFilter(!toggleFilter);
    }

    useEffect(() => {
        getGroups();
        getLevels();
    }, []);

    useEffect(() => {
        getFilters();
    }, [levels]);

    useEffect(() => postFilter(), [toggleFilter]);

    // Handlers ------------------------------------

    const handleFilter = filter => {
        const newFilters = [...filters];
        const i = newFilters.indexOf(filter)

        if (i > -1) {
            newFilters.splice(i, 1)
        } else {
            newFilters.push(filter)
        }

        setFilters(newFilters);
    }

    const handleGroup = id => {
        const newChecked = [...groupsChecked]
        const i = newChecked.indexOf(id)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setGroupsChecked(newChecked);
    }

    const handleLevel = id => {
        const newChecked = [...levelsChecked]
        const i = newChecked.indexOf(id)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setLevelsChecked(newChecked);
    }

    const handleLevelChildren = (addChildren, ids) => {
        let newChecked = [...levelsChecked]

        if (addChildren) {
            newChecked = [...new Set(newChecked.concat(ids))];
        } else {
            newChecked = newChecked.filter((el) => !ids.includes(el));
        }

        setLevelsChecked(newChecked);
    }

    const handleClose = () => {
        onClose();
    }

    // Posters -----------------------------

    const postFilter = () => {

        const data = {}

        if (filters.indexOf('groups') > -1 && groupsChecked.length > 0) {
            data.groups = groupsChecked;
        }

        if (filters.indexOf('membership_levels') > -1 && levelsChecked.length > 0) {
            data.levels = levelsChecked;
        }

        if (filters.indexOf('deleted') > -1) {
            data.deleted = true;
        }

        console.log(data);

        onFilter(data);
        onClose();
    }

    const postClear = () => {
        setLevelsChecked([]);
        setGroupsChecked([]);
        setFilters([]);
        onFilter({});
        onClose();
    }

    // Utility Methods -----------------------

    return (
        <Dialog
            maxWidth="xs"
            open={open}
            onClose={handleClose}
            fullWidth
            aria-labelledby="filter-users"
            >
            <DialogTitle className={classes.dialogSpacing}>
                <Grid container alignItems="center" justify="space-between">
                    <Grid item>
                        <Button color="primary" onClick={postClear} variant="outlined">
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        Filters
                    </Grid>
                    <Grid item>
                        <Button color="primary" variant="contained" onClick={postFilter}>
                            Filter
                        </Button>
                    </Grid>
                </Grid>
            </DialogTitle>
            <Divider />
            <DialogContent className={classes.noContentPadding}>
                <FormControlLabel
                    className={classes.noMarginLeft}
                    control={
                        <Checkbox
                            checked={filters.indexOf('groups') > -1}
                            onChange={() => handleFilter('groups')}
                            name="groups"
                            color="primary"
                        />
                    }
                    label="Groups"
                />
                <Divider />
                <Collapse in={filters.indexOf('groups') > -1} classes={{entered: classes.entered}}>
                    {groupsLoading ? (
                        <div className={classes.subSectionLoading}>
                            <CircularProgress
                                color="primary"
                                size={32}
                                thickness={2}
                            />
                        </div>
                    ) : groups.length ? (
                        <FormGroup className={classes.subSection}>
                            {groups && groups.map(group => (
                                <FormControlLabel
                                    className={classes.noMarginLeft}
                                    key={group.id}
                                    control={
                                        <Checkbox
                                            checked={(groupsChecked.indexOf(group.id) > -1)}
                                            onChange={()=>handleGroup(group.id)}
                                            name={group.title}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label={group.title}
                                />
                            ))}
                        </FormGroup>
                    ) : (
                        <div className={classes.subSection}>
                            <Typography
                                variant="subtitle2"
                                color="textSecondary"
                                className={classes.empty}
                                align="center"
                                >
                                No Available Groups
                            </Typography>
                        </div>
                    )}
                </Collapse>
                <FormControlLabel
                    className={classes.noMarginLeft}
                    control={
                        <Checkbox
                            checked={filters.indexOf('membership_levels') > -1}
                            onChange={() => handleFilter('membership_levels')}
                            name="membership_levels"
                            color="primary"
                        />
                    }
                    label="Membership Levels"
                />
                <Divider />
                <Collapse in={filters.indexOf('membership_levels') > -1} classes={{entered: classes.entered}}>
                    {levelsLoading ? (
                        <div className={classes.subSectionLoading}>
                            <CircularProgress
                                color="primary"
                                size={35}
                                thickness={1.8}
                            />
                        </div>
                    ) : levels.length ? (
                        <List className={classes.subSectionList}>
                            {levels.map(level => (
                                <NestedLevelItem
                                    key={level.id}
                                    level={level}
                                    depth={1}
                                    levelsChecked={levelsChecked}
                                    onCheck={handleLevel}
                                    onCheckChildren={handleLevelChildren}
                                />
                            ))}
                        </List>
                    ) : (
                        <div className={classes.subSection}>
                            <Typography
                                variant="subtitle2"
                                color="textSecondary"
                                className={classes.empty}
                                align="center"
                                >
                                No Available Levels
                            </Typography>
                        </div>
                    )}
                </Collapse>
                <FormControlLabel
                    className={classes.noMarginLeft}
                    control={
                        <Checkbox
                            checked={filters.indexOf('deleted') > -1}
                            onChange={() => handleFilter('deleted')}
                            name="deleted"
                            color="primary"
                        />
                    }
                    label="Include Deleted Users?"
                />
            </DialogContent>
        </Dialog>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFull(theme),
    },
}));

const defaultColumns = [
    { id: 'id', align: 'left', disablePadding: false, label: 'ID' },
    { id: 'membership_title', align: 'left', disablePadding: false, label: 'Membership' },
    { id: 'full_name', align: 'left', disablePadding: true, label: 'Full Name' },
    { id: 'prefix', align: 'left', disablePadding: true, label: 'Prefix' },
    { id: 'first', align: 'left', disablePadding: true, label: 'First' },
    { id: 'middle', align: 'left', disablePadding: true, label: 'Middle' },
    { id: 'last', align: 'left', disablePadding: true, label: 'Last' },
    { id: 'suffix', align: 'left', disablePadding: true, label: 'Suffix' },
    { id: 'nickname', align: 'left', disablePadding: true, label: 'Nickname' },
    { id: 'salutation', align: 'left', disablePadding: true, label: 'Salutation' },
    { id: 'birth_date', align: 'left', disablePadding: true, label: 'Birth Date' },
    { id: 'email', align: 'left', disablePadding: true, label: 'Email', type: 'email', },
    { id: 'phone_mobile', align: 'left', disablePadding: false, label: 'Mobile Phone', type: 'phone' },
    { id: 'home_email', align: 'left', disablePadding: false, label: 'Home Email', type: 'email' },
    { id: 'phone_home', align: 'left', disablePadding: false, label: 'Home Phone', type: 'phone' },
    { id: 'home_mobile', align: 'left', disablePadding: false, label: 'Home Mobile', type: 'phone' },
    { id: 'home_fax', align: 'left', disablePadding: false, label: 'Home Fax' },
    { id: 'address_one_number', align: 'left', disablePadding: false, label: 'Address One Number' },
    { id: 'address_one_street', align: 'left', disablePadding: false, label: 'Address One Street' },
    { id: 'address_one', align: 'left', disablePadding: false, label: 'Address One' },
    { id: 'address_two', align: 'left', disablePadding: false, label: 'Address Two' },
    { id: 'city', align: 'left', disablePadding: false, label: 'City' },
    { id: 'state', align: 'left', disablePadding: false, label: 'State' },
    { id: 'zip', align: 'left', disablePadding: false, label: 'Zip' },
    { id: 'employer', align: 'left', disablePadding: false, label: 'Employer' },
    { id: 'position', align: 'left', disablePadding: false, label: 'Position' },
    { id: 'work_email', align: 'left', disablePadding: false, label: 'Work Email', type: 'email' },
    { id: 'phone_work', align: 'left', disablePadding: false, label: 'Work Phone', type: 'phone' },
    { id: 'work_fax', align: 'left', disablePadding: false, label: 'Work Fax' },
    { id: 'work_address_one', align: 'left', disablePadding: false, label: 'Work Address One' },
    { id: 'work_address_two', align: 'left', disablePadding: false, label: 'Work Address Two' },
    { id: 'work_city', align: 'left', disablePadding: false, label: 'Work City' },
    { id: 'work_state', align: 'left', disablePadding: false, label: 'Work State' },
    { id: 'work_zip', align: 'left', disablePadding: false, label: 'Work Zip' },
    { id: 'snailmail_to', align: 'left', disablePadding: false, label: 'Preferred Address' },
    { id: 'preferred_address_one', align: 'left', disablePadding: false, label: 'Preferred Address One' },
    { id: 'preferred_address_two', align: 'left', disablePadding: false, label: 'Preferred Address Two' },
    { id: 'preferred_city', align: 'left', disablePadding: false, label: 'Preferred City' },
    { id: 'preferred_state', align: 'left', disablePadding: false, label: 'Preferred State' },
    { id: 'preferred_zip', align: 'left', disablePadding: false, label: 'Preferred Zip' },
    { id: 'marital_status', align: 'left', disablePadding: true, label: 'Marital Status' },
    { id: 'spouse', align: 'left', disablePadding: true, label: 'Spouse' },
    { id: 'spouse_nickname', align: 'left', disablePadding: true, label: 'Spouse Nickname' },
    { id: 'spouse_email', align: 'left', disablePadding: true, label: 'Spouse Email', type: 'email' },
    { id: 'spouse_mobile', align: 'left', disablePadding: true, label: 'Spouse Mobile', type: 'phone' },
    { id: 'spouse_company', align: 'left', disablePadding: true, label: 'Spouse Company' },
];

const cavalierColumns = [
    { id: 'river_position', align: 'left', disablePadding: false, label: 'River Position' },
    { id: 'board_member', align: 'left', disablePadding: true, label: 'Board Member' },
    { id: 'king_gift_group', align: 'left', disablePadding: true, label: 'King Gift Group' },
    { id: 'kings_council', align: 'left', disablePadding: true, label: 'Kings Council' },
    { id: 'tccf_past_president', align: 'left', disablePadding: true, label: 'TCCF Past President' },
    { id: 'year_joined', align: 'left', disablePadding: true, label: 'Year Joined' },
    { id: 'military_branch', align: 'left', disablePadding: false, label: 'Military Branch' },
    { id: 'military_rank', align: 'left', disablePadding: false, label: 'Military Rank' },
    { id: 'military_years_of_service', align: 'left', disablePadding: false, label: 'Military Years of Service' },
];

const dateColumns = [
    {
        id: 'created_at',
        align: 'left',
        disablePadding: false,
        label: 'Date Created',
        type: 'date',
        format: 'D'
    },
    {
        id: 'updated_at',
        align: 'left',
        disablePadding: false,
        label: 'Last Updated',
        type: 'date',
        format: 'D'
    },
    {
        id: 'deleted_at',
        align: 'left',
        disablePadding: false,
        label: 'Date Deleted',
        type: 'date',
        format: 'D'
    },
]

export default function Users(props) {

    const { app } = useApp();

    const [ importDialogOpen, setImportDialogOpen ] = useState(false);
    const [ columns, setColumns ] = useState(defaultColumns);

    const classes = useStyles();

    useEffect(() => {
        let additionalColumns = [];

        // Cavalier column addition
        if (app.club.id === 5 || app.user.id === 1) {
            additionalColumns = cavalierColumns
        }

        /* Custom fields (currently not working)
        app.club.fields?.forEach(f => {

            if (tableFieldTypes.indexOf(f.options.type) > -1) {
                additionalColumns.push({
                    id: f.table_id,
                    label: f.options.label,
                    align: (f.options.type === 'number' || f.options.type === 'currency') ? 'right' : 'left',
                    disablePadding: false,
                    type: f.options.type,
                    is_value: true,
                    format: f.options.format
                })
            }
        });
        */

        additionalColumns = additionalColumns.concat(dateColumns);

        setColumns(columns.concat(additionalColumns))
    }, [])

    // Importing
    const handleImportOpen = () => {
        setImportDialogOpen(true);
    }

    const handleImportClose = () => {
        setImportDialogOpen(false);
    }

    return (
        <div className={classes.container}>
            <PaginatedTable
                columns={columns}
                stateKey="users"
                label="Users"
                tableUrl='/user'
                baseUrl='/admin/user'
                viewUrl='/admin/users'
                exportTitle='users-export'
                exportAllTitle='users-export-all'
                onImport={handleImportOpen}
                FilterDialog={<UserFilterDialog />}
                NewDialog={<NewUserDialog />}
                fullHeight
            />
            <UserImportDialog open={importDialogOpen} onClose={handleImportClose}/>
        </div>
    );
}
